import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import AudioDictation from '../../components/widgets/AudioDictation';
import { 
  Calendar,
  Clock, 
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Video,
  Phone,
  Building2,
  User,
  Users,
  Filter,
  Settings,
  Bell,
  X,
  Check,
  Edit,
  Trash2,
  MapPin,
  Link2,
  FileText,
  MessageSquare,
  Play,
  RefreshCw,
  CalendarDays,
  CalendarCheck,
  LayoutGrid,
  List,
  Maximize2,
  Minimize2,
  ExternalLink,
  Save,
  Mic,
  Upload,
  Lock,
  Briefcase
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const FULL_DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7h-19h

const APPOINTMENT_TYPES = [
  { value: 'agency', label: 'RDV en agence', icon: Building2, color: 'bg-blue-500' },
  { value: 'call', label: 'Call conférence', icon: Video, color: 'bg-green-500' },
  { value: 'phone', label: 'Appel téléphonique', icon: Phone, color: 'bg-orange-500' },
  { value: 'task', label: 'Tâche programmée', icon: FileText, color: 'bg-yellow-500' },
];

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 heure' },
  { value: 90, label: '1h30' },
  { value: 120, label: '2 heures' },
  { value: 180, label: '3 heures' },
  { value: 240, label: '4 heures' },
];

const CalendarPage = ({ embedded = false }) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isAgency = user?.role === 'agency' || user?.role === 'project_manager' || user?.role === 'customer_service';
  
  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // week, month, 3days
  const [appointments, setAppointments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cellSize, setCellSize] = useState('normal'); // small, normal, large
  const [showFilters, setShowFilters] = useState(false);
  const [visibleMembers, setVisibleMembers] = useState([]);
  const [showLegend, setShowLegend] = useState(true);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-switch to 3-day view on mobile
      if (mobile && view === 'week') {
        setView('3days');
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Touch resize state for mobile
  const [touchResizing, setTouchResizing] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  
  // Dialogs
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showRecurringBlock, setShowRecurringBlock] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Form state
  const [appointmentForm, setAppointmentForm] = useState({
    client_id: '',
    account_manager_id: user?.id || '',
    date: '',
    time: '09:00',
    duration_minutes: 30,
    appointment_type: 'call',
    subject: '',
    description: '',
    project_id: '',
    meeting_link: '',
    send_invitation: true,
    custom_client_name: '',  // For "other" appointments without a client in system
    is_custom_appointment: false
  });

  // Get filtered projects based on selected client
  const filteredProjects = appointmentForm.client_id 
    ? projects.filter(p => p.client_id === appointmentForm.client_id)
    : projects;

  // Recurring block form
  const [recurringForm, setRecurringForm] = useState({
    title: 'Pause déjeuner',
    start_time: '12:00',
    end_time: '14:00',
    days: ['lun', 'mar', 'mer', 'jeu', 'ven'],
    color: '#94a3b8',
    is_personal: false,
    block_type: 'work',
    is_recurring: true,  // true = récurrent, false = ponctuel
    specific_date: ''    // Date spécifique pour les créneaux ponctuels
  });
  
  // Recurring blocks
  const [recurringBlocks, setRecurringBlocks] = useState([]);
  
  // Drag & Drop state
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  
  // Google Calendar integration
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({ connected: false, email: null });
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  
  // Edit and delete appointment
  const [isEditingAppointment, setIsEditingAppointment] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAppointment, setDeletingAppointment] = useState(false);
  const [savingAppointment, setSavingAppointment] = useState(false);
  
  // Summary form for completed appointments
  const [showSummaryForm, setShowSummaryForm] = useState(false);
  const [summaryForm, setSummaryForm] = useState({ summary: '', recording_link: '' });
  const [savingSummary, setSavingSummary] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchData();
    checkGoogleCalendarStatus();
    
    // Check URL params for Google connection status
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_connected') === 'true') {
      toast.success('Google Calendar connecté avec succès ! Vos RDV seront maintenant synchronisés.');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('google_error')) {
      toast.error('Erreur lors de la connexion à Google Calendar');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Refetch on date change
  useEffect(() => {
    fetchData();
  }, [currentDate]);

  // Check Google Calendar connection status
  const checkGoogleCalendarStatus = async () => {
    try {
      const res = await api.get('/calendar/status');
      setGoogleCalendarStatus(res.data);
    } catch (e) {
      console.error('Error checking Google Calendar status:', e);
    }
  };

  // Connect to Google Calendar
  const connectGoogleCalendar = async () => {
    setConnectingGoogle(true);
    try {
      const res = await api.get('/calendar/auth/url');
      window.location.href = res.data.authorization_url;
    } catch (e) {
      console.error('Error getting Google auth URL:', e);
      toast.error('Erreur lors de la connexion à Google Calendar');
      setConnectingGoogle(false);
    }
  };

  // Disconnect Google Calendar
  const disconnectGoogleCalendar = async () => {
    try {
      await api.post('/calendar/disconnect');
      setGoogleCalendarStatus({ connected: false, email: null });
      toast.success('Google Calendar déconnecté');
    } catch (e) {
      console.error('Error disconnecting Google Calendar:', e);
      toast.error('Erreur lors de la déconnexion');
    }
  };

  // Start editing an appointment
  const startEditingAppointment = () => {
    if (!selectedAppointment) return;
    setEditForm({
      date: selectedAppointment.requested_date || selectedAppointment.date,
      time: selectedAppointment.requested_time || selectedAppointment.time,
      duration_minutes: selectedAppointment.duration_minutes || 30,
      subject: selectedAppointment.subject || '',
      description: selectedAppointment.description || '',
      appointment_type: selectedAppointment.appointment_type || 'meeting',
      meeting_link: selectedAppointment.meeting_link || ''
    });
    setIsEditingAppointment(true);
  };

  // Save edited appointment
  const saveEditedAppointment = async () => {
    if (!selectedAppointment) return;
    setSavingAppointment(true);
    try {
      await api.put(`/calendar/appointments/${selectedAppointment.id}`, editForm);
      toast.success('RDV modifié avec succès');
      setIsEditingAppointment(false);
      setShowAppointmentDetail(false);
      fetchData();
    } catch (e) {
      console.error('Error updating appointment:', e);
      toast.error('Erreur lors de la modification du RDV');
    } finally {
      setSavingAppointment(false);
    }
  };

  // Delete appointment
  const deleteAppointment = async () => {
    if (!selectedAppointment) return;
    setDeletingAppointment(true);
    try {
      await api.delete(`/calendar/appointments/${selectedAppointment.id}`);
      toast.success('RDV supprimé avec succès');
      setShowDeleteConfirm(false);
      setShowAppointmentDetail(false);
      fetchData();
    } catch (e) {
      console.error('Error deleting appointment:', e);
      toast.error('Erreur lors de la suppression du RDV');
    } finally {
      setDeletingAppointment(false);
    }
  };

  // ==========================================
  // DRAG & DROP FUNCTIONS
  // ==========================================
  
  const handleDragStart = (e, appointment) => {
    setDraggedEvent(appointment);
    e.dataTransfer.setData('text/plain', appointment.id);
    e.dataTransfer.effectAllowed = 'move';
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggedEvent(null);
    setDragOverCell(null);
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e, date, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const cellKey = `${date}-${hour}`;
    if (dragOverCell !== cellKey) {
      setDragOverCell(cellKey);
    }
  };

  const handleDragLeave = (e) => {
    // Only reset if leaving the cell entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverCell(null);
    }
  };

  const handleDrop = async (e, targetDate, targetHour) => {
    e.preventDefault();
    setDragOverCell(null);
    
    if (!draggedEvent) return;
    
    const newTime = `${String(targetHour).padStart(2, '0')}:00`;
    const newDate = targetDate;
    
    // Don't update if same date and time
    if (draggedEvent.requested_date === newDate && draggedEvent.requested_time === newTime) {
      setDraggedEvent(null);
      return;
    }
    
    try {
      await api.put(`/calendar/appointments/${draggedEvent.id}`, {
        requested_date: newDate,
        requested_time: newTime
      });
      
      toast.success('RDV déplacé avec succès');
      fetchData();
    } catch (error) {
      console.error('Error moving appointment:', error);
      toast.error('Erreur lors du déplacement du RDV');
    }
    
    setDraggedEvent(null);
  };

  // Quick duration update for mobile - add or subtract 15 minutes
  const [updatingDuration, setUpdatingDuration] = useState(false);
  const quickUpdateDuration = async (delta) => {
    if (!selectedAppointment || updatingDuration) return;
    
    const currentDuration = selectedAppointment.duration_minutes || 60;
    const newDuration = Math.max(15, Math.min(240, currentDuration + delta));
    
    if (newDuration === currentDuration) {
      toast.info(delta > 0 ? 'Durée maximum atteinte (4h)' : 'Durée minimum atteinte (15min)');
      return;
    }
    
    setUpdatingDuration(true);
    try {
      await api.put(`/calendar/appointments/${selectedAppointment.id}`, {
        duration_minutes: newDuration
      });
      
      // Update local state
      setSelectedAppointment(prev => ({
        ...prev,
        duration_minutes: newDuration
      }));
      
      toast.success(`Durée: ${newDuration} min`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setUpdatingDuration(false);
    }
  };

  // Quick time update for mobile - move appointment by 15 or 30 minutes
  const [updatingTime, setUpdatingTime] = useState(false);
  const quickUpdateTime = async (deltaMinutes) => {
    if (!selectedAppointment || updatingTime) return;
    
    const currentTime = selectedAppointment.requested_time || selectedAppointment.time || '09:00';
    const [hours, minutes] = currentTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + deltaMinutes;
    
    // Limit to 7:00 - 19:00
    if (totalMinutes < 7 * 60 || totalMinutes >= 19 * 60) {
      toast.info('Horaire hors plage (7h-19h)');
      return;
    }
    
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    const newTime = `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
    
    setUpdatingTime(true);
    try {
      await api.put(`/calendar/appointments/${selectedAppointment.id}`, {
        time: newTime
      });
      
      // Update local state
      setSelectedAppointment(prev => ({
        ...prev,
        requested_time: newTime,
        time: newTime
      }));
      
      toast.success(`Heure: ${newTime}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setUpdatingTime(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch appointments and recurring blocks (these should always work)
      const [appointmentsRes, blocksRes] = await Promise.all([
        api.get('/calendar/appointments').catch(() => ({ data: [] })),
        // If agency, get all blocks (including personal from other members)
        isAgency 
          ? api.get('/calendar/recurring-blocks/all').catch(() => ({ data: [] }))
          : api.get('/calendar/recurring-blocks').catch(() => ({ data: [] }))
      ]);
      
      setAppointments(appointmentsRes.data || []);
      setRecurringBlocks(blocksRes.data || []);
      
      // Fetch team members separately (might fail)
      if (isAgency) {
        try {
          const membersRes = await api.get('/users/', { params: { role: 'team_member,project_manager,customer_service,creative' } });
          setTeamMembers(membersRes.data || []);
        } catch (e) {
          console.error('Error fetching team members:', e);
        }
        
        try {
          const [clientsRes, projectsRes] = await Promise.all([
            api.get('/users/', { params: { role: 'client' } }),
            api.get('/projects/')
          ]);
          setClients(clientsRes.data || []);
          setProjects(projectsRes.data || []);
        } catch (e) {
          console.error('Error fetching clients/projects:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const goToToday = () => setCurrentDate(new Date());
  const goPrev = () => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() - 1);
    else if (view === '3days') d.setDate(d.getDate() - 3);
    else d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };
  const goNext = () => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + 1);
    else if (view === '3days') d.setDate(d.getDate() + 3);
    else d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };

  // Get 3 days for mobile view
  const get3Days = useCallback(() => {
    const today = new Date(currentDate);
    return Array.from({ length: 3 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      return date;
    });
  }, [currentDate]);

  // Get week days
  const getWeekDays = useCallback(() => {
    const d = new Date(currentDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return date;
    });
  }, [currentDate]);

  // Get month days
  const getMonthDays = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startOffset = (firstDay.getDay() + 6) % 7;
    
    // Previous month days
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  }, [currentDate]);

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => {
      const aptDate = apt.requested_date || apt.date;
      return aptDate === dateStr;
    }).filter(apt => {
      if (visibleMembers.length === 0) return true;
      return visibleMembers.includes(apt.account_manager_id);
    });
  };

  // Create appointment
  const handleCreateAppointment = async () => {
    if (!appointmentForm.subject || !appointmentForm.date || !appointmentForm.time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await api.post('/calendar/appointments/create', {
        client_id: appointmentForm.is_custom_appointment ? null : (appointmentForm.client_id || null),
        account_manager_id: appointmentForm.account_manager_id,
        date: appointmentForm.date,
        time: appointmentForm.time,
        duration_minutes: parseInt(appointmentForm.duration_minutes),
        appointment_type: appointmentForm.appointment_type,
        subject: appointmentForm.subject,
        description: appointmentForm.description,
        project_id: appointmentForm.is_custom_appointment ? null : (appointmentForm.project_id || null),
        meeting_link: appointmentForm.meeting_link,
        send_invitation: appointmentForm.is_custom_appointment ? false : appointmentForm.send_invitation,
        custom_client_name: appointmentForm.is_custom_appointment ? appointmentForm.custom_client_name : null
      });
      
      toast.success('RDV créé avec succès');
      setShowNewAppointment(false);
      resetAppointmentForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du RDV');
    }
  };

  const resetAppointmentForm = () => {
    setAppointmentForm({
      client_id: '',
      account_manager_id: user?.id || '',
      date: '',
      time: '09:00',
      duration_minutes: 30,
      appointment_type: 'call',
      subject: '',
      description: '',
      project_id: '',
      meeting_link: '',
      send_invitation: true,
      custom_client_name: '',
      is_custom_appointment: false
    });
  };

  // State for proposing new time
  const [showProposeTime, setShowProposeTime] = useState(false);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedTime, setProposedTime] = useState('');
  const [responseMessage, setResponseMessage] = useState('');

  // Respond to appointment request
  const handleRespondToAppointment = async (action) => {
    if (!selectedAppointment) return;
    
    // Validation for propose_new_time
    if (action === 'propose_new_time' && (!proposedDate || !proposedTime)) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }
    
    try {
      await api.post(`/calendar/appointments/${selectedAppointment.id}/respond`, {
        action,
        meeting_link: appointmentForm.meeting_link || null,
        message: responseMessage || null,
        proposed_date: action === 'propose_new_time' ? proposedDate : null,
        proposed_time: action === 'propose_new_time' ? proposedTime : null
      });
      
      const messages = {
        accept: 'RDV accepté',
        decline: 'RDV refusé',
        propose_new_time: 'Nouvelle date proposée au client'
      };
      
      toast.success(messages[action] || 'Réponse envoyée');
      setShowAppointmentDetail(false);
      setShowProposeTime(false);
      setProposedDate('');
      setProposedTime('');
      setResponseMessage('');
      fetchData();
    } catch (error) {
      console.error('Response error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réponse');
    }
  };

  // Create recurring or one-time block
  const handleCreateRecurringBlock = async () => {
    // Validation selon le type (ponctuel ou récurrent)
    if (!recurringForm.title || !recurringForm.start_time || !recurringForm.end_time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (recurringForm.is_recurring && recurringForm.days.length === 0) {
      toast.error('Veuillez sélectionner au moins un jour pour un créneau récurrent');
      return;
    }
    
    if (!recurringForm.is_recurring && !recurringForm.specific_date) {
      toast.error('Veuillez sélectionner une date pour un créneau ponctuel');
      return;
    }
    
    try {
      // Prepare data
      const blockData = {
        ...recurringForm,
        // For one-time blocks, clear days and use specific_date
        days: recurringForm.is_recurring ? recurringForm.days : [],
        specific_date: recurringForm.is_recurring ? null : recurringForm.specific_date
      };
      
      await api.post('/calendar/recurring-blocks', blockData);
      toast.success(recurringForm.is_recurring ? 'Créneau récurrent créé' : 'Créneau ponctuel créé');
      setShowRecurringBlock(false);
      // Reset form
      setRecurringForm({
        title: 'Pause déjeuner',
        start_time: '12:00',
        end_time: '14:00',
        days: ['lun', 'mar', 'mer', 'jeu', 'ven'],
        color: '#94a3b8',
        is_personal: false,
        block_type: 'work',
        is_recurring: true,
        specific_date: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  // Delete recurring block
  const handleDeleteRecurringBlock = async (blockId) => {
    try {
      await api.delete(`/calendar/recurring-blocks/${blockId}`);
      toast.success('Créneau supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Add summary to appointment
  const handleAddSummary = async () => {
    if (!selectedAppointment || !summaryForm.summary) return;
    
    setSavingSummary(true);
    try {
      await api.post(`/calendar/appointments/${selectedAppointment.id}/add-summary`, {
        summary: summaryForm.summary,
        recording_link: summaryForm.recording_link || null
      });
      toast.success('Synthèse ajoutée');
      setShowSummaryForm(false);
      setSummaryForm({ summary: '', recording_link: '' });
      
      // Update selected appointment locally
      setSelectedAppointment(prev => ({
        ...prev,
        summary: summaryForm.summary,
        recording_link: summaryForm.recording_link,
        status: 'completed'
      }));
      
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de la synthèse');
    } finally {
      setSavingSummary(false);
    }
  };

  // Send reminder email
  const handleSendReminder = async () => {
    if (!selectedAppointment) return;
    
    setSendingReminder(true);
    try {
      await api.post(`/calendar/appointments/${selectedAppointment.id}/send-reminder`);
      toast.success('Rappel envoyé au client');
      
      // Update local state
      setSelectedAppointment(prev => ({
        ...prev,
        reminder_sent_at: new Date().toISOString()
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du rappel');
    } finally {
      setSendingReminder(false);
    }
  };

  // Upload audio and get AI transcription + summary
  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedAppointment) return;
    
    // Validate file type
    const allowedTypes = ['audio/mp3', 'audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/webm', 'audio/m4a', 'video/mp4'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'wav', 'webm'];
    
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(`Format non supporté. Formats acceptés: ${allowedExtensions.join(', ')}`);
      return;
    }
    
    // Check file size (25MB max)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Le fichier audio dépasse la limite de 25MB');
      return;
    }
    
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `/calendar/appointments/${selectedAppointment.id}/transcribe-and-summarize`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Synthèse IA générée avec succès !');
      
      // Update local state with transcript and summary
      setSelectedAppointment(prev => ({
        ...prev,
        summary: response.data.summary,
        transcript: response.data.transcript,
        status: 'completed'
      }));
      
      // Update summary form
      setSummaryForm(prev => ({
        ...prev,
        summary: response.data.summary
      }));
      
      setShowSummaryForm(false);
      fetchData();
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la transcription');
    } finally {
      setTranscribing(false);
    }
  };

  // Check if a time slot has a recurring or one-time block
  const getRecurringBlockForSlot = (date, hour) => {
    const dayNames = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
    const dayName = dayNames[date.getDay()];
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
    
    return recurringBlocks.find(block => {
      // Filter by visible members if filter is active
      if (visibleMembers.length > 0 && block.user_id && !visibleMembers.includes(block.user_id)) {
        return false;
      }
      
      // Check time range
      const blockStart = parseInt(block.start_time.split(':')[0]);
      const blockEnd = parseInt(block.end_time.split(':')[0]);
      const inTimeRange = hour >= blockStart && hour < blockEnd;
      
      if (!inTimeRange) return false;
      
      // Check if it's a one-time block with specific_date
      if (block.specific_date) {
        return block.specific_date === dateStr;
      }
      
      // Otherwise check recurring days
      return block.days?.includes(dayName);
    });
  };

  // Get type info
  const getTypeInfo = (type) => {
    return APPOINTMENT_TYPES.find(t => t.value === type) || APPOINTMENT_TYPES[1];
  };

  // Get member color
  const getMemberColor = (memberId) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-yellow-500'
    ];
    const index = teamMembers.findIndex(m => m.id === memberId);
    // If member not found in teamMembers, use a hash of the ID for consistent color
    if (index === -1) {
      // Use memberId string to generate a consistent index
      const hash = memberId ? memberId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
      return colors[hash % colors.length];
    }
    return colors[index % colors.length];
  };

  // Get hex color for member (for inline styles)
  const getMemberBgColor = (memberId) => {
    const colors = [
      '#3b82f6', '#22c55e', '#a855f7', '#f97316',
      '#ec4899', '#06b6d4', '#6366f1', '#eab308'
    ];
    const index = teamMembers.findIndex(m => m.id === memberId);
    if (index === -1) {
      const hash = memberId ? memberId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
      return colors[hash % colors.length];
    }
    return colors[index % colors.length];
  };

  // Cell size classes - base height per 15 min slot
  const getCellHeight = () => {
    switch (cellSize) {
      case 'small': return 12; // 12px per 15min
      case 'large': return 24; // 24px per 15min
      default: return 16; // 16px per 15min
    }
  };

  // Get appointment height based on duration (in px)
  const getAppointmentHeight = (duration) => {
    const pixelsPerMinute = getCellHeight() / 15;
    return Math.max(duration * pixelsPerMinute, getCellHeight()); // Minimum 15min
  };

  // Get appointment position offset from hour start (in px)
  const getAppointmentOffset = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const minutesInHour = minutes;
    const pixelsPerMinute = getCellHeight() / 15;
    return minutesInHour * pixelsPerMinute;
  };

  // Resizing state
  const [resizingAppointment, setResizingAppointment] = useState(null);
  const [resizeStart, setResizeStart] = useState(null);

  // Handle resize start
  const handleResizeStart = (e, apt) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingAppointment(apt);
    setResizeStart(e.clientY);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  // Handle resize end
  const handleResizeEnd = async (e) => {
    if (!resizingAppointment || !resizeStart) return;
    
    const deltaY = e.clientY - resizeStart;
    const pixelsPerMinute = getCellHeight() / 15;
    const deltaMinutes = Math.round(deltaY / pixelsPerMinute / 15) * 15; // Round to 15 min
    
    if (deltaMinutes !== 0) {
      const currentDuration = resizingAppointment.duration_minutes || resizingAppointment.duration || 60;
      const newDuration = Math.max(15, Math.min(240, currentDuration + deltaMinutes)); // 15min to 4h
      
      try {
        await api.put(`/calendar/appointments/${resizingAppointment.id}`, {
          duration_minutes: newDuration
        });
        toast.success(`Durée modifiée: ${newDuration} min`);
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la modification');
      }
    }
    
    setResizingAppointment(null);
    setResizeStart(null);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Add mouse up listener for resize
  useEffect(() => {
    const handleMouseUp = (e) => {
      if (resizingAppointment) {
        handleResizeEnd(e);
      }
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [resizingAppointment, resizeStart]);

  // Week view component - Enhanced with 15min slots and proportional heights
  const WeekView = () => {
    const weekDays = getWeekDays();
    const today = new Date().toDateString();
    const cellHeightPx = getCellHeight();
    const hourHeight = cellHeightPx * 4; // 4 x 15min slots per hour

    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header with days */}
        <div className="grid grid-cols-8 border-b bg-slate-50">
          <div className="p-2 text-center text-xs font-medium text-slate-500 border-r">
            Heure
          </div>
          {weekDays.map((day, i) => {
            const isToday = day.toDateString() === today;
            return (
              <div 
                key={i} 
                className={cn(
                  "p-2 text-center border-r",
                  isToday && "bg-blue-50"
                )}
              >
                <div className="text-xs text-slate-500">{DAYS_FR[i]}</div>
                <div className={cn(
                  "text-lg font-semibold",
                  isToday ? "text-blue-600" : "text-slate-700"
                )}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <ScrollArea className="flex-1">
          <div className="grid grid-cols-8">
            {HOURS.map(hour => (
              <React.Fragment key={hour}>
                {/* Time label column */}
                <div className="border-r relative" style={{ height: `${hourHeight}px` }}>
                  <div className="absolute top-0 right-2 text-xs text-slate-400 -translate-y-1/2">
                    {hour}:00
                  </div>
                </div>
                
                {/* Day cells - one per day */}
                {weekDays.map((day, dayIndex) => {
                  // Get all appointments for this day that START within this hour
                  const dayAppointments = getAppointmentsForDate(day).filter(apt => {
                    const time = apt.requested_time || apt.time || '09:00';
                    const aptHour = parseInt(time.split(':')[0]);
                    return aptHour === hour;
                  });
                  
                  // Check for recurring block
                  const recurringBlock = getRecurringBlockForSlot(day, hour);
                  const dateStr = day.toISOString().split('T')[0];
                  const cellKey = `${dateStr}-${hour}`;
                  const isDropTarget = dragOverCell === cellKey;
                  
                  return (
                    <div 
                      key={dayIndex}
                      onDragOver={(e) => !recurringBlock && handleDragOver(e, dateStr, hour)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => !recurringBlock && handleDrop(e, dateStr, hour)}
                      className={cn(
                        "border-r relative transition-colors group",
                        recurringBlock 
                          ? "cursor-not-allowed" 
                          : "cursor-pointer",
                        isDropTarget && !recurringBlock && "bg-blue-100 ring-2 ring-blue-400 ring-inset"
                      )}
                      style={{ 
                        height: `${hourHeight}px`,
                        backgroundColor: recurringBlock ? recurringBlock.color + '10' : undefined
                      }}
                    >
                      {/* 15-minute grid lines */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="h-1/4 border-b border-slate-100"></div>
                        <div className="h-1/4 border-b border-slate-200 border-dashed"></div>
                        <div className="h-1/4 border-b border-slate-100"></div>
                        <div className="h-1/4 border-b border-slate-300"></div>
                      </div>

                      {/* Clickable slots for each 15min interval */}
                      {[0, 15, 30, 45].map(minutes => (
                        <div
                          key={minutes}
                          className="absolute left-0 right-0 hover:bg-blue-50/50 transition-colors"
                          style={{ 
                            top: `${(minutes / 15) * cellHeightPx}px`, 
                            height: `${cellHeightPx}px` 
                          }}
                          onClick={() => {
                            if (recurringBlock) return;
                            setAppointmentForm(prev => ({
                              ...prev,
                              date: day.toISOString().split('T')[0],
                              time: `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
                            }));
                            setShowNewAppointment(true);
                          }}
                        />
                      ))}
                      
                      {/* Recurring block indicator */}
                      {recurringBlock && (
                        <div 
                          className={cn(
                            "absolute inset-1 rounded text-[10px] flex items-center justify-center z-10",
                            recurringBlock.is_personal && recurringBlock.user_id !== user?.id
                              ? "text-slate-400 bg-slate-200/50 italic"
                              : "text-slate-500 opacity-60"
                          )}
                          style={{ 
                            backgroundColor: recurringBlock.is_personal && recurringBlock.user_id !== user?.id
                              ? undefined 
                              : recurringBlock.color + '30' 
                          }}
                        >
                          <Lock className={cn(
                            "w-3 h-3 mr-1",
                            recurringBlock.is_personal && recurringBlock.user_id !== user?.id ? "inline" : "hidden"
                          )} />
                          {recurringBlock.is_personal && recurringBlock.user_id !== user?.id
                            ? `${recurringBlock.user_first_name || 'Occupé'}`
                            : recurringBlock.title
                          }
                        </div>
                      )}
                      
                      {/* Appointments with proportional heights */}
                      {dayAppointments.map((apt) => {
                        const typeInfo = getTypeInfo(apt.appointment_type);
                        const TypeIcon = typeInfo.icon;
                        const time = apt.requested_time || apt.time || '09:00';
                        const duration = apt.duration_minutes || apt.duration || 60;
                        const offsetTop = getAppointmentOffset(time);
                        const height = getAppointmentHeight(duration);
                        const isTask = apt.type === 'task' || apt.appointment_type === 'task';
                        const isDragging = draggedEvent?.id === apt.id;
                        
                        return (
                          <div
                            key={apt.id}
                            draggable={isAgency}
                            onDragStart={(e) => handleDragStart(e, apt)}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAppointment(apt);
                              setShowAppointmentDetail(true);
                            }}
                            className={cn(
                              "absolute left-0.5 right-0.5 rounded px-1 py-0.5 text-white text-xs cursor-pointer hover:opacity-90 overflow-hidden z-20 transition-all",
                              getMemberColor(apt.account_manager_id),
                              isTask && "border-l-4 border-l-yellow-400",
                              resizingAppointment?.id === apt.id && "ring-2 ring-blue-400 shadow-lg",
                              isDragging && "opacity-50 scale-95",
                              isAgency && "cursor-grab active:cursor-grabbing"
                            )}
                            style={{ 
                              top: `${offsetTop}px`,
                              height: `${height}px`,
                              minHeight: '20px'
                            }}
                            title={`${apt.subject} (${duration}min)${isAgency ? ' - Glisser pour déplacer' : ''}`}
                          >
                            <div className="flex items-start gap-1 h-full">
                              <TypeIcon className="w-3 h-3 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="truncate font-medium leading-tight">{apt.subject}</div>
                                {height > 30 && (
                                  <div className="truncate text-[10px] opacity-80">
                                    {apt.client_name || apt.custom_client_name}
                                  </div>
                                )}
                                {height > 50 && (
                                  <div className="text-[10px] opacity-70 mt-1">
                                    {time} • {duration}min
                                  </div>
                                )}
                              </div>
                              {isTask && (
                                <Badge variant="secondary" className="text-[8px] px-1 py-0 bg-yellow-100 text-yellow-800">
                                  Tâche
                                </Badge>
                              )}
                            </div>
                            
                            {/* Resize handle at bottom */}
                            <div 
                              className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-black/20 flex items-center justify-center"
                              onMouseDown={(e) => handleResizeStart(e, apt)}
                            >
                              <div className="w-8 h-1 bg-white/50 rounded-full"></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  };

  // 3-Day view component for mobile - with touch resize support
  const ThreeDayView = () => {
    const threeDays = get3Days();
    const today = new Date().toDateString();
    const cellHeightPx = getCellHeight();
    const hourHeight = cellHeightPx * 4; // 4 x 15min slots per hour

    // Touch handlers for resize
    const handleTouchStart = (e, apt) => {
      e.stopPropagation();
      const touch = e.touches[0];
      setTouchResizing(apt);
      setTouchStartY(touch.clientY);
    };

    const handleTouchMove = (e) => {
      if (!touchResizing || !touchStartY) return;
      e.preventDefault();
    };

    const handleTouchEnd = async (e) => {
      if (!touchResizing || !touchStartY) return;
      
      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - touchStartY;
      const pixelsPerMinute = cellHeightPx / 15;
      const deltaMinutes = Math.round(deltaY / pixelsPerMinute / 15) * 15;
      
      if (deltaMinutes !== 0) {
        const currentDuration = touchResizing.duration_minutes || touchResizing.duration || 60;
        const newDuration = Math.max(15, Math.min(240, currentDuration + deltaMinutes));
        
        try {
          await api.put(`/calendar/appointments/${touchResizing.id}`, {
            duration_minutes: newDuration
          });
          toast.success(`Durée: ${newDuration} min`);
          fetchData();
        } catch (error) {
          toast.error('Erreur lors de la modification');
        }
      }
      
      setTouchResizing(null);
      setTouchStartY(null);
    };

    return (
      <div 
        className="flex flex-col h-full overflow-hidden"
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header with 3 days */}
        <div className="grid grid-cols-4 border-b bg-slate-50">
          <div className="p-2 text-center text-xs font-medium text-slate-500 border-r">
            Heure
          </div>
          {threeDays.map((day, i) => {
            const isToday = day.toDateString() === today;
            const dayName = DAYS_FR[day.getDay() === 0 ? 6 : day.getDay() - 1];
            return (
              <div 
                key={i} 
                className={cn(
                  "p-2 text-center border-r",
                  isToday && "bg-blue-50"
                )}
              >
                <div className="text-xs text-slate-500">{dayName}</div>
                <div className={cn(
                  "text-lg font-semibold",
                  isToday ? "text-blue-600" : "text-slate-700"
                )}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <ScrollArea className="flex-1">
          <div className="grid grid-cols-4">
            {HOURS.map(hour => (
              <React.Fragment key={hour}>
                {/* Time label column */}
                <div className="border-r relative" style={{ height: `${hourHeight}px` }}>
                  <div className="absolute top-0 right-1 text-[10px] text-slate-400 -translate-y-1/2">
                    {hour}:00
                  </div>
                </div>
                
                {/* 3 Day cells */}
                {threeDays.map((day, dayIndex) => {
                  const dayAppointments = getAppointmentsForDate(day).filter(apt => {
                    const time = apt.requested_time || apt.time || '09:00';
                    const aptHour = parseInt(time.split(':')[0]);
                    return aptHour === hour;
                  });
                  
                  const recurringBlock = getRecurringBlockForSlot(day, hour);
                  const dateStr = day.toISOString().split('T')[0];
                  const cellKey = `${dateStr}-${hour}`;
                  const isDropTarget = dragOverCell === cellKey;
                  
                  return (
                    <div 
                      key={dayIndex}
                      onDragOver={(e) => !recurringBlock && handleDragOver(e, dateStr, hour)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => !recurringBlock && handleDrop(e, dateStr, hour)}
                      className={cn(
                        "border-r relative transition-colors",
                        recurringBlock ? "cursor-not-allowed" : "cursor-pointer",
                        isDropTarget && !recurringBlock && "bg-blue-100 ring-2 ring-blue-400 ring-inset"
                      )}
                      style={{ 
                        height: `${hourHeight}px`,
                        backgroundColor: recurringBlock ? recurringBlock.color + '10' : undefined
                      }}
                    >
                      {/* 15-minute grid lines */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="h-1/4 border-b border-slate-100"></div>
                        <div className="h-1/4 border-b border-slate-200 border-dashed"></div>
                        <div className="h-1/4 border-b border-slate-100"></div>
                        <div className="h-1/4 border-b border-slate-300"></div>
                      </div>

                      {/* Clickable slots for each 15min interval */}
                      {[0, 15, 30, 45].map(minutes => (
                        <div
                          key={minutes}
                          className="absolute left-0 right-0 active:bg-blue-100"
                          style={{ 
                            top: `${(minutes / 15) * cellHeightPx}px`, 
                            height: `${cellHeightPx}px` 
                          }}
                          onClick={() => {
                            if (recurringBlock) return;
                            setAppointmentForm(prev => ({
                              ...prev,
                              date: day.toISOString().split('T')[0],
                              time: `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
                            }));
                            setShowNewAppointment(true);
                          }}
                        />
                      ))}
                      
                      {/* Recurring block indicator */}
                      {recurringBlock && (
                        <div 
                          className={cn(
                            "absolute inset-1 rounded text-[10px] flex items-center justify-center z-10",
                            recurringBlock.is_personal && recurringBlock.user_id !== user?.id
                              ? "text-slate-400 bg-slate-200/50 italic"
                              : "text-slate-500 opacity-60"
                          )}
                          style={{ 
                            backgroundColor: recurringBlock.is_personal && recurringBlock.user_id !== user?.id
                              ? undefined 
                              : recurringBlock.color + '30' 
                          }}
                        >
                          <Lock className={cn(
                            "w-3 h-3 mr-1",
                            recurringBlock.is_personal && recurringBlock.user_id !== user?.id ? "inline" : "hidden"
                          )} />
                          {recurringBlock.is_personal && recurringBlock.user_id !== user?.id
                            ? `${recurringBlock.user_first_name || 'Occupé'}`
                            : recurringBlock.title
                          }
                        </div>
                      )}
                      
                      {/* Appointments with proportional heights */}
                      {dayAppointments.map((apt) => {
                        const typeInfo = getTypeInfo(apt.appointment_type);
                        const TypeIcon = typeInfo.icon;
                        const time = apt.requested_time || apt.time || '09:00';
                        const duration = apt.duration_minutes || apt.duration || 60;
                        const offsetTop = getAppointmentOffset(time);
                        const height = getAppointmentHeight(duration);
                        const isTask = apt.type === 'task' || apt.appointment_type === 'task';
                        const isDragging = draggedEvent?.id === apt.id;
                        
                        return (
                          <div
                            key={apt.id}
                            draggable={isAgency}
                            onDragStart={(e) => handleDragStart(e, apt)}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAppointment(apt);
                              setShowAppointmentDetail(true);
                            }}
                            className={cn(
                              "absolute left-0.5 right-0.5 rounded px-1 py-0.5 text-white text-[10px] cursor-pointer overflow-hidden z-20 transition-all",
                              getMemberColor(apt.account_manager_id),
                              isTask && "border-l-4 border-l-yellow-400",
                              touchResizing?.id === apt.id && "ring-2 ring-blue-400 shadow-lg scale-105",
                              isDragging && "opacity-50 scale-95",
                              isAgency && "cursor-grab active:cursor-grabbing"
                            )}
                            style={{ 
                              top: `${offsetTop}px`,
                              height: `${height}px`,
                              minHeight: '24px'
                            }}
                          >
                            <div className="flex items-start gap-1 h-full">
                              <TypeIcon className="w-3 h-3 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="truncate font-medium leading-tight">{apt.subject}</div>
                                {height > 40 && (
                                  <div className="text-[9px] opacity-70">
                                    {time} • {duration}min
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Touch resize handle at bottom - larger for mobile */}
                            <div 
                              className="absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize flex items-center justify-center touch-none"
                              style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}
                              onTouchStart={(e) => handleTouchStart(e, apt)}
                              onMouseDown={(e) => handleResizeStart(e, apt)}
                            >
                              <div className="w-10 h-1.5 bg-white/70 rounded-full"></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  };

  // Month view component
  const MonthView = () => {
    const monthDays = getMonthDays();
    const today = new Date().toDateString();

    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="grid grid-cols-7 border-b bg-slate-50">
          {DAYS_FR.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 border-r">
              {day}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="flex-1 grid grid-cols-7">
          {monthDays.map(({ date, isCurrentMonth }, idx) => {
            const isToday = date.toDateString() === today;
            const dayAppointments = getAppointmentsForDate(date);
            
            return (
              <div 
                key={idx}
                className={cn(
                  "border-r border-b p-1 min-h-[100px] cursor-pointer hover:bg-slate-50",
                  !isCurrentMonth && "bg-slate-50/50"
                )}
                onClick={() => {
                  setAppointmentForm(prev => ({
                    ...prev,
                    date: date.toISOString().split('T')[0]
                  }));
                  setShowNewAppointment(true);
                }}
              >
                <div className={cn(
                  "text-sm font-medium mb-1",
                  !isCurrentMonth && "text-slate-400",
                  isToday && "w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center"
                )}>
                  {date.getDate()}
                </div>
                <div className="space-y-0.5">
                  {dayAppointments.slice(0, 3).map(apt => {
                    const typeInfo = getTypeInfo(apt.appointment_type);
                    return (
                      <div
                        key={apt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAppointment(apt);
                          setShowAppointmentDetail(true);
                        }}
                        className={cn(
                          "text-xs px-1 py-0.5 rounded truncate text-white",
                          getMemberColor(apt.account_manager_id)
                        )}
                      >
                        {apt.requested_time || apt.time} {apt.subject}
                      </div>
                    );
                  })}
                  {dayAppointments.length > 3 && (
                    <div className="text-xs text-slate-500 px-1">
                      +{dayAppointments.length - 3} autres
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Planning view (list)
  const PlanningView = () => {
    const upcomingAppointments = appointments
      .filter(apt => {
        const aptDate = new Date(apt.requested_date || apt.date);
        return aptDate >= new Date();
      })
      .sort((a, b) => {
        const dateA = new Date(a.requested_date || a.date);
        const dateB = new Date(b.requested_date || b.date);
        return dateA - dateB;
      })
      .slice(0, 20);

    return (
      <ScrollArea className="h-full">
        <div className="space-y-3 p-4">
          {upcomingAppointments.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Aucun RDV à venir</p>
            </div>
          ) : (
            upcomingAppointments.map(apt => {
              const typeInfo = getTypeInfo(apt.appointment_type);
              const TypeIcon = typeInfo.icon;
              const member = teamMembers.find(m => m.id === apt.account_manager_id);
              
              return (
                <Card 
                  key={apt.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    setSelectedAppointment(apt);
                    setShowAppointmentDetail(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={cn("p-2 rounded-lg", typeInfo.color)}>
                        <TypeIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold truncate" style={{ color: theme.colors?.primary }}>
                            {apt.subject}
                          </h4>
                          <Badge variant={apt.status === 'accepted' ? 'default' : 'secondary'}>
                            {apt.status === 'pending' ? 'En attente' : 
                             apt.status === 'accepted' ? 'Confirmé' : apt.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(apt.requested_date || apt.date).toLocaleDateString('fr-FR', { 
                              weekday: 'short', day: 'numeric', month: 'short' 
                            })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {apt.requested_time || apt.time} ({apt.duration_minutes || 30}min)
                          </span>
                        </div>
                        {apt.client_name && (
                          <p className="text-sm text-slate-600 mt-1">
                            Client: {apt.client_name}
                          </p>
                        )}
                        {member && (
                          <div className="flex items-center gap-2 mt-2">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={member.avatar_url} />
                              <AvatarFallback className="text-xs">
                                {member.first_name?.[0]}{member.last_name?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-xs text-slate-500">
                              {member.first_name} {member.last_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>
    );
  };

  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      );
    }
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  // Main calendar content
  const calendarContent = (
    <>
    <div className={cn("space-y-4", embedded ? "h-auto" : "h-[calc(100vh-180px)]")}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Agenda
            </h1>
            <p className="text-sm text-slate-500">
              {MONTHS_FR[currentDate.getMonth()]} {currentDate.getFullYear()}
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Navigation */}
            <div className="flex items-center gap-1 bg-white rounded-lg border p-1">
              <Button variant="ghost" size="icon" onClick={goPrev}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={goToToday}>
                Aujourd'hui
              </Button>
              <Button variant="ghost" size="icon" onClick={goNext}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* View selector */}
            <div className="flex items-center border rounded-lg p-1 bg-white">
              {/* 3-day view - shown on mobile, hidden on desktop */}
              <button
                onClick={() => setView('3days')}
                className={cn(
                  "p-2 rounded transition-colors md:hidden",
                  view === '3days' && "bg-slate-100"
                )}
                title="Vue 3 jours"
              >
                <span className="text-xs font-medium">3J</span>
              </button>
              <button
                onClick={() => setView('week')}
                className={cn("p-2 rounded transition-colors", view === 'week' && "bg-slate-100")}
                title="Vue semaine"
              >
                <CalendarDays className="w-4 h-4" />
              </button>
              <button
                onClick={() => setView('month')}
                className={cn("p-2 rounded transition-colors", view === 'month' && "bg-slate-100")}
                title="Vue mois"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setView('planning')}
                className={cn("p-2 rounded transition-colors", view === 'planning' && "bg-slate-100")}
                title="Vue liste"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Cell size - shown for week and 3days views */}
            {(view === 'week' || view === '3days') && (
              <div className="flex items-center border rounded-lg p-1 bg-white">
                <button
                  onClick={() => setCellSize('small')}
                  className={cn("p-2 rounded transition-colors", cellSize === 'small' && "bg-slate-100")}
                  title="Petit"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCellSize('large')}
                  className={cn("p-2 rounded transition-colors", cellSize === 'large' && "bg-slate-100")}
                  title="Grand"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Filters */}
            <Button variant="outline" size="icon" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4" />
            </Button>

            {/* Recurring blocks */}
            <Button variant="outline" onClick={() => setShowRecurringBlock(true)}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Bloquer créneaux
            </Button>

            {/* Google Calendar Status */}
            {googleCalendarStatus.connected ? (
              <Button 
                variant="outline" 
                size="sm"
                className="text-green-600 border-green-300 hover:bg-green-50"
                onClick={() => setShowSettings(true)}
              >
                <Check className="w-4 h-4 mr-2" />
                Google Sync
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={connectGoogleCalendar}
                disabled={connectingGoogle}
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                {connectingGoogle ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4 mr-2" />
                )}
                Sync Google
              </Button>
            )}

            {/* Settings */}
            <Button variant="outline" size="icon" onClick={() => setShowSettings(true)}>
              <Settings className="w-4 h-4" />
            </Button>

            {/* New appointment */}
            <Button onClick={() => setShowNewAppointment(true)} style={{ backgroundColor: theme.colors?.accent }}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau RDV
            </Button>
          </div>
        </div>

        {/* Pending Appointment Requests Banner */}
        {isAgency && appointments.filter(apt => apt.status === 'pending').length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800">
                      {appointments.filter(apt => apt.status === 'pending').length} demande(s) de RDV en attente
                    </p>
                    <p className="text-sm text-amber-600">
                      Des clients attendent votre confirmation
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {appointments
                    .filter(apt => apt.status === 'pending')
                    .slice(0, 3)
                    .map(apt => (
                      <Button
                        key={apt.id}
                        variant="outline"
                        size="sm"
                        className="bg-white border-amber-300 hover:bg-amber-100"
                        onClick={() => {
                          setSelectedAppointment(apt);
                          setShowAppointmentDetail(true);
                        }}
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        {apt.client_name || apt.custom_client_name || 'Client'} - {apt.requested_date || apt.date}
                      </Button>
                    ))
                  }
                  {appointments.filter(apt => apt.status === 'pending').length > 3 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setView('planning')}
                      className="text-amber-700"
                    >
                      +{appointments.filter(apt => apt.status === 'pending').length - 3} autre(s)
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters panel */}
        {showFilters && isAgency && (
          <Card className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Filtrer par membre:
              </Label>
              <div className="flex flex-wrap gap-2">
                {teamMembers.map(member => (
                  <Button
                    key={member.id}
                    variant={visibleMembers.includes(member.id) || visibleMembers.length === 0 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      if (visibleMembers.includes(member.id)) {
                        setVisibleMembers(prev => prev.filter(id => id !== member.id));
                      } else {
                        setVisibleMembers(prev => [...prev, member.id]);
                      }
                    }}
                    className={cn(
                      visibleMembers.includes(member.id) && getMemberColor(member.id),
                      visibleMembers.includes(member.id) && "text-white border-0"
                    )}
                  >
                    {member.first_name} {member.last_name?.[0]}.
                  </Button>
                ))}
                {visibleMembers.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setVisibleMembers([])}>
                    Tous
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Legend - Clickable to filter by member */}
        {showLegend && isAgency && teamMembers.length > 0 && view !== 'planning' && (
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="text-slate-500">Légende:</span>
            {/* All button */}
            <button
              onClick={() => setVisibleMembers([])}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded-full transition-all",
                visibleMembers.length === 0
                  ? "bg-slate-200 text-slate-800"
                  : "hover:bg-slate-100 text-slate-500"
              )}
            >
              <span className="text-xs font-medium">Tous</span>
            </button>
            {teamMembers.slice(0, 8).map(member => {
              const isSelected = visibleMembers.includes(member.id);
              return (
                <button
                  key={member.id}
                  onClick={() => {
                    if (isSelected) {
                      // Remove from filter
                      setVisibleMembers(prev => prev.filter(id => id !== member.id));
                    } else {
                      // Add to filter
                      setVisibleMembers(prev => [...prev, member.id]);
                    }
                  }}
                  className={cn(
                    "flex items-center gap-1.5 px-2 py-1 rounded-full transition-all cursor-pointer",
                    isSelected
                      ? "ring-2 ring-offset-1"
                      : visibleMembers.length > 0
                        ? "opacity-50 hover:opacity-100"
                        : "hover:bg-slate-100"
                  )}
                  style={isSelected ? { 
                    backgroundColor: getMemberBgColor(member.id),
                    ringColor: getMemberBgColor(member.id)
                  } : {}}
                  title={`Cliquez pour ${isSelected ? 'masquer' : 'afficher'} les RDV de ${member.first_name}`}
                >
                  <Avatar className="w-5 h-5">
                    <AvatarImage src={member.avatar_url} />
                    <AvatarFallback className={cn("text-[10px] text-white", getMemberColor(member.id))}>
                      {member.first_name?.[0]}{member.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className={cn(
                    "text-slate-600",
                    isSelected && "font-medium text-slate-900"
                  )}>{member.first_name}</span>
                </button>
              );
            })}
            {visibleMembers.length > 0 && (
              <button
                onClick={() => setVisibleMembers([])}
                className="text-xs text-slate-400 hover:text-slate-600 underline ml-2"
              >
                Réinitialiser
              </button>
            )}
          </div>
        )}

        {/* Calendar view */}
        <Card className="flex-1 overflow-hidden">
          {view === 'week' && <WeekView />}
          {view === '3days' && <ThreeDayView />}
          {view === 'month' && <MonthView />}
          {view === 'planning' && <PlanningView />}
        </Card>
      </div>

      {/* New Appointment Dialog */}
      <Dialog open={showNewAppointment} onOpenChange={setShowNewAppointment}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>Nouveau RDV</DialogTitle>
            <DialogDescription>Planifiez un nouveau rendez-vous</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Type de RDV */}
            <div className="space-y-2">
              <Label>Type de RDV *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {APPOINTMENT_TYPES.map(type => {
                  const TypeIcon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setAppointmentForm(prev => ({ ...prev, appointment_type: type.value }))}
                      className={cn(
                        "flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-3 rounded-lg border transition-all",
                        appointmentForm.appointment_type === type.value
                          ? "border-2 bg-slate-50"
                          : "hover:bg-slate-50"
                      )}
                      style={appointmentForm.appointment_type === type.value ? { borderColor: theme.colors?.accent } : {}}
                    >
                      <div className={cn("p-1.5 sm:p-2 rounded-full", type.color)}>
                        <TypeIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                      </div>
                      <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sujet */}
            <div className="space-y-2">
              <Label>Sujet *</Label>
              <Input
                value={appointmentForm.subject}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Ex: Point projet, Présentation..."
              />
            </div>

            {/* Date et heure */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Heure *</Label>
                <Input
                  type="time"
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>

            {/* Durée */}
            <div className="space-y-2">
              <Label>Durée</Label>
              <Select 
                value={String(appointmentForm.duration_minutes)} 
                onValueChange={(v) => setAppointmentForm(prev => ({ ...prev, duration_minutes: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Client (agency only) */}
            {isAgency && (
              <div className="space-y-3">
                {/* Toggle between client in system or custom */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 p-3 bg-slate-50 rounded-lg">
                  <Button
                    type="button"
                    variant={!appointmentForm.is_custom_appointment ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAppointmentForm(prev => ({ 
                      ...prev, 
                      is_custom_appointment: false,
                      custom_client_name: '',
                      project_id: ''
                    }))}
                    className="flex-1"
                    style={!appointmentForm.is_custom_appointment ? { backgroundColor: theme.colors?.accent } : {}}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Client existant
                  </Button>
                  <Button
                    type="button"
                    variant={appointmentForm.is_custom_appointment ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAppointmentForm(prev => ({ 
                      ...prev, 
                      is_custom_appointment: true,
                      client_id: '',
                      project_id: ''
                    }))}
                    className="flex-1"
                    style={appointmentForm.is_custom_appointment ? { backgroundColor: theme.colors?.accent } : {}}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Autre (libre)
                  </Button>
                </div>

                {/* Client from system */}
                {!appointmentForm.is_custom_appointment && (
                  <div className="space-y-2">
                    <Label>Client *</Label>
                    {clients.length > 0 ? (
                      <Select 
                        value={appointmentForm.client_id || 'none'} 
                        onValueChange={(v) => setAppointmentForm(prev => ({ 
                          ...prev, 
                          client_id: v === 'none' ? '' : v,
                          project_id: '' // Reset project when client changes
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un client" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Sélectionner un client --</SelectItem>
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.company_name || `${client.first_name} ${client.last_name}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-slate-500 italic">Aucun client dans le système. Utilisez l&apos;option &quot;Autre (libre)&quot;.</p>
                    )}
                  </div>
                )}

                {/* Custom client name (free text) */}
                {appointmentForm.is_custom_appointment && (
                  <div className="space-y-2">
                    <Label>Nom du contact / entreprise</Label>
                    <Input
                      value={appointmentForm.custom_client_name}
                      onChange={(e) => setAppointmentForm(prev => ({ ...prev, custom_client_name: e.target.value }))}
                      placeholder="Ex: Jean Dupont - Société XYZ"
                    />
                    <p className="text-xs text-slate-500">
                      Pour un RDV externe ou un prospect non encore dans le système
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Projet lié (only if client selected from system) */}
            {isAgency && !appointmentForm.is_custom_appointment && appointmentForm.client_id && (
              <div className="space-y-2">
                <Label>Projet associé (optionnel)</Label>
                <Select 
                  value={appointmentForm.project_id || 'none'} 
                  onValueChange={(v) => setAppointmentForm(prev => ({ ...prev, project_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un projet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun projet spécifique</SelectItem>
                    {filteredProjects.length > 0 ? (
                      filteredProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>Aucun projet pour ce client</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {filteredProjects.length === 0 && appointmentForm.client_id && (
                  <p className="text-xs text-amber-600">Ce client n&apos;a pas encore de projet</p>
                )}
              </div>
            )}

            {/* Lien de réunion */}
            {appointmentForm.appointment_type === 'call' && (
              <div className="space-y-2">
                <Label>Lien de réunion (Google Meet, Zoom...)</Label>
                <Input
                  value={appointmentForm.meeting_link}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                  placeholder="https://meet.google.com/..."
                />
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={appointmentForm.description}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ordre du jour, points à aborder..."
                rows={3}
              />
            </div>

            {/* Envoyer invitation */}
            {isAgency && appointmentForm.client_id && !appointmentForm.is_custom_appointment && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Envoyer une invitation au client</Label>
                  <p className="text-xs text-slate-500">Un email sera envoyé avec les détails du RDV</p>
                </div>
                <Switch
                  checked={appointmentForm.send_invitation}
                  onCheckedChange={(checked) => setAppointmentForm(prev => ({ ...prev, send_invitation: checked }))}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAppointment(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateAppointment}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              Créer le RDV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Detail Dialog */}
      <Dialog open={showAppointmentDetail} onOpenChange={(open) => {
        setShowAppointmentDetail(open);
        if (!open) {
          setIsEditingAppointment(false);
          setShowDeleteConfirm(false);
        }
      }}>
        <DialogContent className="max-w-lg">
          {selectedAppointment && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  {(() => {
                    const typeInfo = getTypeInfo(selectedAppointment.appointment_type);
                    const TypeIcon = typeInfo.icon;
                    return <TypeIcon className="w-5 h-5" />;
                  })()}
                  {isEditingAppointment ? 'Modifier le RDV' : selectedAppointment.subject}
                </DialogTitle>
              </DialogHeader>
              
              {/* Delete confirmation */}
              {showDeleteConfirm ? (
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-red-800 font-medium">Êtes-vous sûr de vouloir supprimer ce RDV ?</p>
                    <p className="text-sm text-red-600 mt-1">Cette action est irréversible.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                      onClick={deleteAppointment}
                      disabled={deletingAppointment}
                    >
                      {deletingAppointment ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Supprimer
                    </Button>
                  </div>
                </div>
              ) : isEditingAppointment ? (
                /* Edit form */
                <div className="space-y-4 py-2">
                  <div>
                    <Label>Sujet</Label>
                    <Input
                      value={editForm.subject}
                      onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={editForm.date}
                        onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Heure</Label>
                      <Input
                        type="time"
                        value={editForm.time}
                        onChange={(e) => setEditForm(prev => ({ ...prev, time: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Durée (minutes)</Label>
                      <Input
                        type="number"
                        value={editForm.duration_minutes}
                        onChange={(e) => setEditForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <select
                        value={editForm.appointment_type}
                        onChange={(e) => setEditForm(prev => ({ ...prev, appointment_type: e.target.value }))}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="meeting">Réunion</option>
                        <option value="call">Appel</option>
                        <option value="video">Visio</option>
                        <option value="onsite">Sur site</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Lien de réunion</Label>
                    <Input
                      value={editForm.meeting_link}
                      onChange={(e) => setEditForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                      placeholder="https://meet.google.com/..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setIsEditingAppointment(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="flex-1"
                      style={{ backgroundColor: theme.colors?.accent }}
                      onClick={saveEditedAppointment}
                      disabled={savingAppointment}
                    >
                      {savingAppointment ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Enregistrer
                    </Button>
                  </div>
                </div>
              ) : (
              /* Normal view */
              <div className="space-y-4">
                {/* Edit/Delete buttons for agency/managers */}
                {(isAgency || selectedAppointment.account_manager_id === user?.id) && (
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={startEditingAppointment}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <Badge variant={selectedAppointment.status === 'accepted' ? 'default' : 'secondary'}>
                    {selectedAppointment.status === 'pending' ? 'En attente de confirmation' : 
                     selectedAppointment.status === 'pending_client' ? 'En attente du client' :
                     selectedAppointment.status === 'pending_reschedule' ? 'Replanification demandée' :
                     selectedAppointment.status === 'accepted' ? 'Confirmé' : selectedAppointment.status}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    {getTypeInfo(selectedAppointment.appointment_type).label}
                  </span>
                </div>

                {/* Date and Time with quick edit buttons for mobile */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Date</Label>
                    <p className="font-medium">
                      {new Date(selectedAppointment.requested_date || selectedAppointment.date).toLocaleDateString('fr-FR', {
                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                      })}
                    </p>
                  </div>
                  
                  {/* Time with quick adjustment buttons */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-slate-500">Heure</Label>
                      <p className="font-medium">
                        {selectedAppointment.requested_time || selectedAppointment.time}
                      </p>
                    </div>
                    {(isAgency || selectedAppointment.account_manager_id === user?.id) && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => quickUpdateTime(-15)}
                          disabled={updatingTime}
                          title="-15 min"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-xs text-slate-500 w-12 text-center">±15min</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => quickUpdateTime(15)}
                          disabled={updatingTime}
                          title="+15 min"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Duration with quick adjustment buttons */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-slate-500">Durée</Label>
                      <p className="font-medium">
                        {selectedAppointment.duration_minutes || 30} min
                      </p>
                    </div>
                    {(isAgency || selectedAppointment.account_manager_id === user?.id) && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-red-600 hover:bg-red-50"
                          onClick={() => quickUpdateDuration(-15)}
                          disabled={updatingDuration}
                        >
                          -15
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-green-600 hover:bg-green-50"
                          onClick={() => quickUpdateDuration(15)}
                          disabled={updatingDuration}
                        >
                          +15
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-green-600 hover:bg-green-50"
                          onClick={() => quickUpdateDuration(30)}
                          disabled={updatingDuration}
                        >
                          +30
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {selectedAppointment.client_name && (
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <User className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-medium">{selectedAppointment.client_name}</p>
                      <p className="text-sm text-slate-500">{selectedAppointment.client_company || 'Client'}</p>
                    </div>
                  </div>
                )}

                {selectedAppointment.meeting_link && (
                  <a 
                    href={selectedAppointment.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Video className="w-5 h-5" />
                    <span>Rejoindre la réunion</span>
                    <ExternalLink className="w-4 h-4 ml-auto" />
                  </a>
                )}

                {selectedAppointment.description && (
                  <div>
                    <Label className="text-xs text-slate-500">Notes</Label>
                    <p className="text-sm mt-1">{selectedAppointment.description}</p>
                  </div>
                )}

                {/* Summary section for completed/past appointments */}
                {(selectedAppointment.status === 'completed' || selectedAppointment.status === 'accepted') && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-700 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Synthèse du RDV
                      </Label>
                      {!showSummaryForm && !selectedAppointment.summary && isAgency && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowSummaryForm(true)}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Ajouter
                        </Button>
                      )}
                    </div>
                    
                    {selectedAppointment.summary ? (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm whitespace-pre-wrap">{selectedAppointment.summary}</p>
                        {selectedAppointment.transcript && (
                          <details className="mt-3">
                            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                              Voir la transcription complète
                            </summary>
                            <p className="mt-2 text-xs text-slate-600 whitespace-pre-wrap border-t pt-2">
                              {selectedAppointment.transcript}
                            </p>
                          </details>
                        )}
                        {selectedAppointment.recording_link && (
                          <a 
                            href={selectedAppointment.recording_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Voir l'enregistrement
                          </a>
                        )}
                      </div>
                    ) : showSummaryForm ? (
                      <div className="space-y-3">
                        {/* Audio Dictation Component */}
                        <AudioDictation
                          appointmentId={selectedAppointment.id}
                          clientId={selectedAppointment.client_id}
                          initialText={summaryForm.summary}
                          placeholder="Dictez ou tapez votre compte-rendu de RDV..."
                          showSaveButton={false}
                          onTranscriptionComplete={(newText, fullText) => {
                            setSummaryForm(prev => ({ ...prev, summary: fullText }));
                          }}
                        />
                        
                        <Input
                          placeholder="Lien vers l'enregistrement vidéo (optionnel)"
                          value={summaryForm.recording_link}
                          onChange={(e) => setSummaryForm(prev => ({ ...prev, recording_link: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setShowSummaryForm(false);
                              setSummaryForm({ summary: '', recording_link: '' });
                            }}
                          >
                            Annuler
                          </Button>
                          <Button 
                            onClick={handleAddSummary}
                            disabled={!summaryForm.summary || savingSummary}
                            style={{ backgroundColor: theme.colors?.accent }}
                          >
                            {savingSummary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Enregistrer
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic">Aucune synthèse ajoutée</p>
                    )}
                  </div>
                )}

                {/* Send reminder button for accepted appointments */}
                {isAgency && selectedAppointment.status === 'accepted' && (
                  <div className="pt-4 border-t">
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={handleSendReminder}
                      disabled={sendingReminder}
                    >
                      {sendingReminder ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Bell className="w-4 h-4 mr-2" />
                      )}
                      Envoyer un rappel au client
                    </Button>
                    {selectedAppointment.reminder_sent_at && (
                      <p className="text-xs text-slate-400 mt-2 text-center">
                        Dernier rappel envoyé le {new Date(selectedAppointment.reminder_sent_at).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                )}

                {/* Actions for pending_client appointments - Agency can force accept */}
                {isAgency && selectedAppointment.status === 'pending_client' && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        En attente de validation par le client
                      </p>
                    </div>
                    
                    <Button 
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                      onClick={async () => {
                        try {
                          await api.post(`/calendar/appointments/${selectedAppointment.id}/force-accept`);
                          toast.success('RDV confirmé pour le client');
                          setShowAppointmentDetail(false);
                          fetchEvents();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erreur');
                        }
                      }}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accepté client (forcer la validation)
                    </Button>
                    
                    <p className="text-xs text-slate-500 text-center">
                      Utilisez cette option si le client a confirmé par téléphone ou email
                    </p>
                  </div>
                )}

                {/* Actions for pending appointments */}
                {isAgency && selectedAppointment.status === 'pending' && (
                  <div className="space-y-4 pt-4 border-t">
                    <Label className="text-base font-semibold">Répondre à la demande</Label>
                    
                    {/* Meeting link field */}
                    <div className="space-y-2">
                      <Label className="text-sm">Lien de réunion (optionnel)</Label>
                      <Input
                        placeholder="https://meet.google.com/... ou https://zoom.us/..."
                        value={appointmentForm.meeting_link}
                        onChange={(e) => setAppointmentForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                      />
                    </div>
                    
                    {/* Quick accept/decline buttons */}
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                        onClick={() => handleRespondToAppointment('accept')}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Accepter
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleRespondToAppointment('decline')}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Refuser
                      </Button>
                    </div>
                    
                    {/* Propose new time section */}
                    <div className="border-t pt-4">
                      {!showProposeTime ? (
                        <Button 
                          variant="outline"
                          className="w-full"
                          onClick={() => setShowProposeTime(true)}
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Proposer une autre date/heure
                        </Button>
                      ) : (
                        <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold text-blue-800">Proposer un nouveau créneau</Label>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setShowProposeTime(false);
                                setProposedDate('');
                                setProposedTime('');
                                setResponseMessage('');
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-blue-700">Nouvelle date *</Label>
                              <Input
                                type="date"
                                value={proposedDate}
                                onChange={(e) => setProposedDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="mt-1 bg-white"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-blue-700">Nouvelle heure *</Label>
                              <Input
                                type="time"
                                value={proposedTime}
                                onChange={(e) => setProposedTime(e.target.value)}
                                className="mt-1 bg-white"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-xs text-blue-700">Message au client (optionnel)</Label>
                            <Textarea
                              placeholder="Ex: Je ne suis pas disponible à cette date, voici une alternative..."
                              value={responseMessage}
                              onChange={(e) => setResponseMessage(e.target.value)}
                              rows={2}
                              className="mt-1 bg-white"
                            />
                          </div>
                          
                          <Button 
                            className="w-full"
                            style={{ backgroundColor: theme.colors?.accent }}
                            onClick={() => handleRespondToAppointment('propose_new_time')}
                            disabled={!proposedDate || !proposedTime}
                          >
                            <Calendar className="w-4 h-4 mr-2" />
                            Envoyer la proposition
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Paramètres de l'agenda</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Afficher la légende</Label>
                <p className="text-xs text-slate-500">Montre les membres dans la légende</p>
              </div>
              <Switch
                checked={showLegend}
                onCheckedChange={setShowLegend}
              />
            </div>
            
            <div className="pt-4 border-t">
              <Label className="text-slate-700 font-medium">Synchronisation Google Calendar</Label>
              
              {googleCalendarStatus.connected ? (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Connecté</p>
                      <p className="text-sm text-green-600">{googleCalendarStatus.email}</p>
                    </div>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    Vos RDV acceptés sont automatiquement synchronisés avec votre Google Calendar.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={disconnectGoogleCalendar}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Déconnecter
                  </Button>
                </div>
              ) : (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600">
                    Connectez votre Google Calendar pour synchroniser automatiquement vos RDV et les retrouver avec vos événements personnels.
                  </p>
                  <Button 
                    className="mt-3 bg-blue-600 hover:bg-blue-700"
                    onClick={connectGoogleCalendar}
                    disabled={connectingGoogle}
                  >
                    {connectingGoogle ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Calendar className="w-4 h-4 mr-2" />
                    )}
                    Connecter Google Calendar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recurring Blocks Dialog */}
      <Dialog open={showRecurringBlock} onOpenChange={setShowRecurringBlock}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>Bloquer un créneau</DialogTitle>
            <DialogDescription>Bloquez des créneaux ponctuels ou récurrents (pauses, réunions...)</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="create">
            <TabsList className="w-full">
              <TabsTrigger value="create" className="flex-1">Créer</TabsTrigger>
              <TabsTrigger value="list" className="flex-1">Mes créneaux ({recurringBlocks.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="create" className="space-y-4 mt-4">
              {/* Ponctuel ou Récurrent */}
              <div className="space-y-2">
                <Label>Type de blocage</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setRecurringForm(prev => ({ ...prev, is_recurring: false, days: [] }))}
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      !recurringForm.is_recurring 
                        ? "border-amber-500 bg-amber-50" 
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarCheck className="w-4 h-4 text-amber-600" />
                      <span className="font-medium text-sm">Ponctuel</span>
                    </div>
                    <p className="text-xs text-slate-500">Une seule date</p>
                  </button>
                  
                  <button
                    onClick={() => setRecurringForm(prev => ({ ...prev, is_recurring: true, specific_date: '' }))}
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      recurringForm.is_recurring 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-sm">Récurrent</span>
                    </div>
                    <p className="text-xs text-slate-500">Chaque semaine</p>
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={recurringForm.title}
                  onChange={(e) => setRecurringForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ex: Pause déjeuner, Réunion équipe..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Heure début *</Label>
                  <Input
                    type="time"
                    value={recurringForm.start_time}
                    onChange={(e) => setRecurringForm(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure fin *</Label>
                  <Input
                    type="time"
                    value={recurringForm.end_time}
                    onChange={(e) => setRecurringForm(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>
              
              {/* Date spécifique pour ponctuel */}
              {!recurringForm.is_recurring && (
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={recurringForm.specific_date}
                    onChange={(e) => setRecurringForm(prev => ({ ...prev, specific_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              )}
              
              {/* Jours pour récurrent */}
              {recurringForm.is_recurring && (
                <div className="space-y-2">
                  <Label>Jours *</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'lun', label: 'Lun' },
                      { value: 'mar', label: 'Mar' },
                      { value: 'mer', label: 'Mer' },
                      { value: 'jeu', label: 'Jeu' },
                      { value: 'ven', label: 'Ven' },
                      { value: 'sam', label: 'Sam' },
                      { value: 'dim', label: 'Dim' },
                    ].map(day => (
                      <button
                        key={day.value}
                        onClick={() => {
                          setRecurringForm(prev => ({
                            ...prev,
                            days: prev.days.includes(day.value)
                              ? prev.days.filter(d => d !== day.value)
                              : [...prev.days, day.value]
                          }));
                        }}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-sm transition-colors",
                          recurringForm.days.includes(day.value)
                            ? "bg-slate-800 text-white"
                            : "bg-slate-100 hover:bg-slate-200"
                        )}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Couleur</Label>
                <div className="flex gap-2">
                  {['#94a3b8', '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#6366f1', '#ec4899'].map(color => (
                    <button
                      key={color}
                      onClick={() => setRecurringForm(prev => ({ ...prev, color }))}
                      className={cn(
                        "w-8 h-8 rounded-full transition-transform",
                        recurringForm.color === color && "ring-2 ring-offset-2 ring-slate-400 scale-110"
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Block type selection */}
              <div className="space-y-2">
                <Label>Type de créneau</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setRecurringForm(prev => ({ 
                      ...prev, 
                      is_personal: false,
                      block_type: 'work'
                    }))}
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      !recurringForm.is_personal 
                        ? "border-blue-500 bg-blue-50" 
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">Professionnel</span>
                    </div>
                    <p className="text-xs text-slate-500">Visible par tous (réunion, formation...)</p>
                  </button>
                  
                  <button
                    onClick={() => setRecurringForm(prev => ({ 
                      ...prev, 
                      is_personal: true,
                      block_type: 'personal',
                      color: '#9ca3af'
                    }))}
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      recurringForm.is_personal 
                        ? "border-slate-500 bg-slate-50" 
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className="w-4 h-4 text-slate-600" />
                      <span className="font-medium text-sm">Personnel</span>
                    </div>
                    <p className="text-xs text-slate-500">Privé (les autres voient juste "Occupé")</p>
                  </button>
                </div>
              </div>
              
              <Button 
                onClick={handleCreateRecurringBlock}
                className="w-full"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {recurringForm.is_recurring ? 'Créer le créneau récurrent' : 'Créer le créneau ponctuel'}
              </Button>
            </TabsContent>
            
            <TabsContent value="list" className="mt-4">
              {recurringBlocks.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Aucun créneau configuré
                </div>
              ) : (
                <div className="space-y-2">
                  {recurringBlocks.filter(b => b.user_id === user?.id || !b.is_personal).map(block => (
                    <div 
                      key={block.id} 
                      className="flex items-center justify-between p-3 rounded-lg border"
                      style={{ borderLeftColor: block.color, borderLeftWidth: 4 }}
                    >
                      <div className="flex items-center gap-3">
                        {block.is_personal ? (
                          <Lock className="w-4 h-4 text-slate-400" />
                        ) : (
                          <Briefcase className="w-4 h-4 text-blue-500" />
                        )}
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {block.title}
                            {block.is_personal && (
                              <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Privé</span>
                            )}
                            {block.specific_date && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Ponctuel</span>
                            )}
                          </p>
                          <p className="text-sm text-slate-500">
                            {block.start_time} - {block.end_time} • 
                            {block.specific_date 
                              ? ` ${new Date(block.specific_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`
                              : ` ${block.days?.join(', ') || ''}`
                            }
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRecurringBlock(block.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );

  // Return with or without MainLayout based on embedded mode
  if (embedded) {
    return calendarContent;
  }
  return <MainLayout>{calendarContent}</MainLayout>;
};

export default CalendarPage;
